
import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { FaPhoneAlt } from "react-icons/fa";
import { MdPhoneCallback, MdSupportAgent } from "react-icons/md";
import { IoBanSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const InboundDashboard = ({ setView, user }) => {
    const [data, setData] = useState({
        totalCalls: 0,
        successfulResolutions: 0,
        callbackRequested: 0,
        missedCalls: 0,
    });

    const getStartOfMonth = () => {
        const now = new Date();
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
            .toISOString()
            .split("T")[0];
    };

    const [fromDate, setFromDate] = useState(getStartOfMonth());
    const [toDate, setToDate] = useState("");

    const [rawData, setRawData] = useState({
        calls: [],
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (!user) return;
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        filterData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate, rawData]);

    const fetchData = async () => {
        const userId = user.id || user._id;
        console.log(`Fetching inbound data for Dashboard. UserID: ${userId}, Role: ${user.role}`);
        try {
            // Fetch inbound call history
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_API}/api/call-history?userId=${userId}&role=${user.role}&direction=inbound`
            );
            const callsData = await response.json();

            setRawData({
                calls: callsData || [],
            });

        } catch (error) {
            console.error("Error fetching inbound data:", error);
        }
    };

    const filterData = () => {
        const from = new Date(fromDate);
        const today = new Date();
        const to = toDate
            ? new Date(new Date(toDate).setHours(23, 59, 59, 999))
            : new Date(today.setHours(23, 59, 59, 999));

        const rangeFiltered = rawData.calls.filter((call) => {
            const callDate = new Date(call.createdAt);
            return callDate >= from && callDate <= to;
        });

        const totalCalls = rangeFiltered.length;
        const successfulResolutions = rangeFiltered.filter(c => c.qaStatus === "Positive").length;
        const missedCalls = rangeFiltered.filter(c => !c.agentSentiment).length;
        const callbackRequested = 0; // Placeholder until we have specific inbound waiting list logic

        setData({
            totalCalls,
            successfulResolutions,
            callbackRequested,
            missedCalls,
        });
    };

    const COLORS = {
        totalCalls: "#3B82F6",
        successfulResolutions: "#10B981",
        callbackRequested: "#F59E0B",
        missedCalls: "#EF4444",
    };

    const dataPie = [
        { name: "Total Inbound", value: data.totalCalls, color: COLORS.totalCalls },
        { name: "Resolved", value: data.successfulResolutions, color: COLORS.successfulResolutions },
        { name: "Missed", value: data.missedCalls, color: COLORS.missedCalls },
    ].filter(item => item.value > 0);

    const dataBar = [
        { category: "Total Inbound", count: data.totalCalls, color: COLORS.totalCalls },
        { category: "Resolved", count: data.successfulResolutions, color: COLORS.successfulResolutions },
        { category: "Missed", count: data.missedCalls, color: COLORS.missedCalls },
    ].filter(item => item.count > 0);

    const handleTotalCallsClick = () => setView("CallHistory");

    return (
        <div className="bg-gray-50 p-4 md:p-6 lg:p-8">
            <div className="mx-auto space-y-8">
                <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Inbound Calls Dashboard</h2>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="flex flex-col w-full sm:w-auto">
                        <label className="text-gray-700 font-semibold mb-1">From Date</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="w-full sm:w-48 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        />
                    </div>
                    <div className="flex flex-col w-full sm:w-auto">
                        <label className="text-gray-700 font-semibold mb-1">To Date</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="w-full sm:w-48 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        />
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                        { icon: <FaPhoneAlt className="text-3xl" style={{ color: COLORS.totalCalls }} />, label: "Total Inbound", value: data.totalCalls, onClick: handleTotalCallsClick },
                        { icon: <MdSupportAgent className="text-3xl" style={{ color: COLORS.successfulResolutions }} />, label: "Successfully Resolved", value: data.successfulResolutions },
                        { icon: <MdPhoneCallback className="text-3xl" style={{ color: COLORS.callbackRequested }} />, label: "Callback Requested", value: data.callbackRequested },
                        { icon: <IoBanSharp className="text-3xl" style={{ color: COLORS.missedCalls }} />, label: "Missed Calls", value: data.missedCalls },
                    ].map((item, index) => (
                        <div
                            key={index}
                            className={`p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 ${item.onClick ? "cursor-pointer hover:bg-gray-50" : ""}`}
                            onClick={item.onClick || null}
                        >
                            <div className="flex items-center space-x-4">
                                {item.icon}
                                <div>
                                    <p className="text-sm font-medium text-gray-600">{item.label}</p>
                                    <p className="text-2xl font-bold text-gray-900">{item.value}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-xl shadow-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">Inbound Call Distribution</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie data={dataPie} dataKey="value" cx="50%" cy="50%" outerRadius={120} innerRadius={60} label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                                    {dataPie.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">Inbound Performance Metrics</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={dataBar}>
                                <XAxis dataKey="category" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="count">
                                    {dataBar.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InboundDashboard;
